import type { NpcSnap, Role } from '../net/protocol';
import { COMBAT } from '../net/protocol';
import {
  NPC_TUNING, PERSONALITIES, rollPersonality,
  stageFromMood, saturationFactor,
  type NpcPersonality,
} from './npc_aggro';
import type { AABB2 } from './arenas';

type ServerNpc = {
  id: number;
  x: number;
  z: number;
  yaw: number;
  home: { x: number; z: number };
  wanderX: number;
  wanderZ: number;
  wanderPause: number;
  mood: number;              // signed, unbounded
  prevStage: 0 | 1 | 2 | 3;
  respawnAtMs: number;       // >0 while hidden between lives
  shinyUntilMs: number;
  // Personality (randomized on spawn). Affects movement feel below
  // saturation; IGNORED at stage 3 so farming exploits can't leverage
  // a slow archetype.
  personality: NpcPersonality;
  // Anti-stuck tracking — if the NPC has barely moved while trying to
  // flee/cling, re-roll the escape-angle bias.
  lastCheckMs: number;
  lastX: number;
  lastZ: number;
  escapeAngleBias: number;   // radians added to natural flee/cling dir
};

export type AttackerInfo = {
  id: string;
  role: Role;
  x: number;
  z: number;
  hood: boolean;
  absorptionMult: number;    // 1.0 for starter weapons, items bump this up later
};

export type NpcXpGrant = { attackerId: string; amount: number };
export type NpcShoutEvent = { npcId: number; x: number; z: number; mood: number };

export class NpcManager {
  private npcs: ServerNpc[] = [];
  private nextId = 1;
  // CP6 — per-mode override. Defaults to NPC_TUNING.COUNT until the
  // server overrides via setTargetCount() at warmup→playing.
  private targetCount: number = NPC_TUNING.COUNT;
  // Per-arena spawn box. Defaults to the legacy Classic-80u box until
  // the server calls setSpawnBounds(arena.npcBounds) at warmup.
  private spawnBounds: AABB2 = NPC_TUNING.SPAWN_BOUNDS;

  constructor() {
    for (let i = 0; i < this.targetCount; i++) this.npcs.push(this.makeNpc());
  }

  private makeNpc(): ServerNpc {
    const x = randRange(this.spawnBounds.xMin, this.spawnBounds.xMax);
    const z = randRange(this.spawnBounds.zMin, this.spawnBounds.zMax);
    return {
      id: this.nextId++,
      x, z, yaw: 0,
      home: { x, z },
      wanderX: x, wanderZ: z,
      wanderPause: Math.random() * 2,
      mood: 0,
      prevStage: 0,
      respawnAtMs: 0,
      shinyUntilMs: 0,
      personality: rollPersonality(),
      lastCheckMs: 0,
      lastX: x,
      lastZ: z,
      escapeAngleBias: 0,
    };
  }

  // Set per-mode NPC headcount. Race wants ~30, Classic 10, PvE 14.
  // Idempotent — adjusts the live array length without touching state
  // of NPCs that survive the resize.
  setTargetCount(count: number): void {
    this.targetCount = Math.max(0, count);
    while (this.npcs.length > this.targetCount) this.npcs.pop();
    while (this.npcs.length < this.targetCount) this.npcs.push(this.makeNpc());
  }

  // Set per-arena spawn + leave bounds. Must be called BEFORE
  // setTargetCount() at warmup→playing so fresh NPCs spawn in-bounds.
  setSpawnBounds(b: AABB2): void {
    this.spawnBounds = b;
  }

  resetAll() {
    this.npcs = [];
    for (let i = 0; i < this.targetCount; i++) this.npcs.push(this.makeNpc());
  }

  // Debug-only: drop an NPC at the given XZ. Bumps the target count so
  // resetAll() / setTargetCount(prev) won't immediately erase it.
  forceSpawnAt(x: number, z: number, shinyMs = 0): number {
    const npc: ServerNpc = {
      id: this.nextId++,
      x, z, yaw: 0,
      home: { x, z },
      wanderX: x, wanderZ: z,
      wanderPause: Math.random() * 2,
      mood: 0,
      prevStage: 0,
      respawnAtMs: 0,
      shinyUntilMs: shinyMs > 0 ? Date.now() + shinyMs : 0,
      personality: rollPersonality(),
      lastCheckMs: 0,
      lastX: x,
      lastZ: z,
      escapeAngleBias: 0,
    };
    this.npcs.push(npc);
    this.targetCount = Math.max(this.targetCount, this.npcs.length);
    return npc.id;
  }

  getTargetCount(): number {
    return this.targetCount;
  }

  tick(
    dt: number,
    nowMs: number,
    attackers: AttackerInfo[],
    onDamagePlayer: (id: string, dmg: number) => { killed: boolean },
  ): { xp: NpcXpGrant[]; shouts: NpcShoutEvent[] } {
    const xp: NpcXpGrant[] = [];
    const shouts: NpcShoutEvent[] = [];

    for (const npc of this.npcs) {
      // Respawn cooldown: NPC is hidden + inert until the timer elapses,
      // then we roll a brand new one in the same array slot.
      if (npc.respawnAtMs > 0) {
        if (nowMs >= npc.respawnAtMs) {
          const fresh = this.makeNpc();
          const keepId = npc.id;
          Object.assign(npc, fresh);
          npc.id = keepId;
          npc.respawnAtMs = 0;
        } else {
          continue;
        }
      }

      // Find all absorbers overlapping this NPC (both factions can stack).
      const inRange: AttackerInfo[] = [];
      for (const a of attackers) {
        if (NPC_TUNING.HOOD_BLOCKS_ABSORPTION && a.role === 'int' && a.hood) continue;
        const d = Math.hypot(a.x - npc.x, a.z - npc.z);
        const ok = a.role === 'ext'
          ? d <= COMBAT.CHAD_CIRCLE_R
          : d >= COMBAT.VIRGIN_RING_INNER && d <= COMBAT.VIRGIN_RING_OUTER;
        if (ok) inRange.push(a);
      }

      // Apply absorption, dampened by saturation. Each new unit is worth
      // less the more drained the NPC already is — soft diminishing return
      // that replaces the old hard cap. No clamp on mood.
      const sat = saturationFactor(npc.mood);
      let absMagnitude = 0;
      for (const a of inRange) {
        const delta = NPC_TUNING.BASE_ABSORPTION_PER_SEC * a.absorptionMult * sat * dt;
        if (a.role === 'ext') npc.mood -= delta;
        else                  npc.mood += delta;
        absMagnitude += delta;
      }

      // Homeostasis: exponential drift toward neutral. Also unbounded above
      // but in practice the saturation curve keeps mood well under ±300.
      npc.mood *= Math.max(0, 1 - NPC_TUNING.NORMALIZATION_PER_SEC * dt);

      const stage = stageFromMood(npc.mood);

      // XP flows whenever someone is actively absorbing. Skilled players
      // can stretch farm duration by herding the NPC away from the map
      // edge — there's no stage cutoff, only the natural saturation curve
      // reducing each tick's yield as the NPC gets drained.
      if (absMagnitude > 0) {
        const mult = nowMs < npc.shinyUntilMs ? NPC_TUNING.SHINY_MULT : 1;
        const amount = absMagnitude * NPC_TUNING.XP_PER_ABSORPTION_UNIT * mult;
        const share = amount / inRange.length;
        for (const a of inRange) xp.push({ attackerId: a.id, amount: share });
      }

      // Rising-edge shout when NPC first crosses into stage 2 this tick.
      if (stage >= 2 && npc.prevStage < 2) {
        shouts.push({ npcId: npc.id, x: npc.x, z: npc.z, mood: npc.mood });
      }
      npc.prevStage = stage;

      this.moveNpc(npc, dt, nowMs, stage, inRange);

      // Contact body-check damage at stage 2/3 — reckless leeching hurts,
      // this is the push-back against infinite-farm exploits.
      if (stage >= 2 && inRange.length > 0) {
        const dps = NPC_TUNING.CONTACT_DPS[stage];
        for (const a of inRange) {
          const d = Math.hypot(a.x - npc.x, a.z - npc.z);
          if (d > NPC_TUNING.CONTACT_R) continue;
          const { killed } = onDamagePlayer(a.id, dps * dt);
          if (killed) {
            const shinyActive = this.npcs.reduce((n, x) => n + (x.shinyUntilMs > nowMs ? 1 : 0), 0);
            if (shinyActive < NPC_TUNING.SHINY_CAP) {
              npc.shinyUntilMs = nowMs + NPC_TUNING.SHINY_SEC * 1000;
            }
            // Calm down after a kill — mood resets so the NPC doesn't
            // immediately sprint off.
            npc.mood = 0;
            break;
          }
        }
      }

      // Physical exit check — the only way NPCs actually leave. If a
      // player chased them off the edge while farming, they're gone and
      // will respawn fresh at a new random spawn point.
      const b = this.spawnBounds;
      const m = NPC_TUNING.LEAVE_MARGIN;
      if (npc.x < b.xMin - m || npc.x > b.xMax + m ||
          npc.z < b.zMin - m || npc.z > b.zMax + m) {
        npc.respawnAtMs = nowMs + NPC_TUNING.RESPAWN_DELAY_SEC * 1000;
      }
    }

    return { xp, shouts };
  }

  private moveNpc(
    npc: ServerNpc,
    dt: number,
    nowMs: number,
    stage: 0 | 1 | 2 | 3,
    inRange: AttackerInfo[],
  ) {
    let targetX: number;
    let targetZ: number;
    let speed: number;

    const pers = PERSONALITIES[npc.personality];
    // At saturation (stage 3) we STRIP personality modifiers — the NPC
    // must use the full base escape speed so farming exploits can't
    // leverage a slow "lazy" archetype.
    const saturated = stage === 3;
    const speedMult = saturated ? 1 : pers.speedMult;
    const wanderMult = saturated ? 1 : pers.wanderMult;

    if (stage >= 1) {
      // Direction of travel driven by mood polarity, not by whoever is
      // closest right now. A fleeing NPC keeps fleeing the faction that
      // drained them even if they briefly leave the weapon shape.
      const wantRole: Role = npc.mood < 0 ? 'ext' : 'int';
      let target: { x: number; z: number } | null = null;
      let bestD = Infinity;
      for (const a of inRange) {
        if (a.role !== wantRole) continue;
        const d = Math.hypot(a.x - npc.x, a.z - npc.z);
        if (d < bestD) { bestD = d; target = { x: a.x, z: a.z }; }
      }
      if (!target) {
        // Nobody of the right faction in range — drift back to wander.
        targetX = npc.wanderX;
        targetZ = npc.wanderZ;
        speed = NPC_TUNING.SPEED[0] * speedMult;
      } else if (npc.mood < 0) {
        // Flee: move away from target. Apply personality/erratic/anti-
        // stuck angle bias so NPCs routes around obstacles instead of
        // walking straight into the same wall every tick.
        const dx = npc.x - target.x;
        const dz = npc.z - target.z;
        const d = Math.hypot(dx, dz) || 1;
        let angle = Math.atan2(dz, dx) + npc.escapeAngleBias;
        // Erratic personality adds a small continuous jitter to the
        // flee direction so the path is zigzag-human, not straight.
        if (pers.erraticJitter && !saturated) {
          angle += (Math.random() - 0.5) * 0.9;
        }
        targetX = npc.x + Math.cos(angle) * 10;
        targetZ = npc.z + Math.sin(angle) * 10;
        speed = NPC_TUNING.SPEED[stage] * speedMult;
      } else {
        // Cling: move toward target. Angle bias still applies so
        // clingy NPCs don't pile up at the exact same spot.
        const dx = target.x - npc.x;
        const dz = target.z - npc.z;
        const d = Math.hypot(dx, dz) || 1;
        let angle = Math.atan2(dz, dx) + npc.escapeAngleBias;
        if (pers.erraticJitter && !saturated) {
          angle += (Math.random() - 0.5) * 0.6;
        }
        targetX = npc.x + Math.cos(angle) * Math.max(0.5, d);
        targetZ = npc.z + Math.sin(angle) * Math.max(0.5, d);
        speed = NPC_TUNING.SPEED[stage] * speedMult;
      }
    } else {
      // Stage 0 — wander around home. Previously NPCs cycled pause →
      // walk → pause on a tight ~1 s rhythm which made the crowd
      // look robotic (everyone shuffling in lockstep). Now:
      //   - Pause durations are longer on average (1.5-4 s base)
      //   - Occasionally a long "idle" pause (5-10 s) fires so some
      //     NPCs just hang out while others roam — reads natural.
      //   - Personality wanderMult scales both pause + radius so
      //     lazy NPCs hang around and erratic ones roam wider.
      if (npc.wanderPause > 0) {
        npc.wanderPause -= dt;
        return;
      }
      const dxH = npc.wanderX - npc.x;
      const dzH = npc.wanderZ - npc.z;
      if (Math.hypot(dxH, dzH) < 0.6) {
        const a = Math.random() * Math.PI * 2;
        const r = Math.random() * NPC_TUNING.WANDER_R * wanderMult;
        npc.wanderX = npc.home.x + Math.cos(a) * r;
        npc.wanderZ = npc.home.z + Math.sin(a) * r;
        // 25% chance a "long idle" — NPC stays put for 5-10 s. The
        // other 75% is a normal 1.5-4 s pause. Both scale inversely
        // with personality speedMult (lazy personalities pause
        // longer, aggressive shorter).
        const longIdle = Math.random() < 0.25;
        const pauseBase = longIdle
          ? 5 + Math.random() * 5
          : 1.5 + Math.random() * 2.5;
        npc.wanderPause = pauseBase * (2 - pers.speedMult);
        return;
      }
      targetX = npc.wanderX;
      targetZ = npc.wanderZ;
      speed = NPC_TUNING.SPEED[0] * speedMult;
    }

    const dx = targetX - npc.x;
    const dz = targetZ - npc.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.05) return;
    const step = speed * dt;
    const k = Math.min(step, d) / d;
    const beforeX = npc.x;
    const beforeZ = npc.z;
    npc.x += dx * k;
    npc.z += dz * k;
    npc.yaw = Math.atan2(dx, dz);

    // ---- Anti-stuck check ----
    // If the NPC is trying to flee/cling (stage >= 1) but barely moved
    // over STUCK_WINDOW_MS, re-roll its escape-angle bias so the next
    // frame routes around whatever wall/prop pinned it. Decay the bias
    // slowly when it's actively moving so normal pathing doesn't
    // accumulate drift.
    if (stage >= 1) {
      if (npc.lastCheckMs === 0 || nowMs - npc.lastCheckMs >= NPC_TUNING.STUCK_WINDOW_MS) {
        const moved = Math.hypot(npc.x - npc.lastX, npc.z - npc.lastZ);
        if (moved < NPC_TUNING.STUCK_MIN_DELTA) {
          // Stuck — random ± 120° kick on the escape bias.
          const kick = (Math.random() - 0.5) * 2 * (Math.PI * 2 / 3);
          npc.escapeAngleBias += kick;
          // Keep bias bounded so we don't drift past ±π.
          if (npc.escapeAngleBias > Math.PI) npc.escapeAngleBias -= Math.PI * 2;
          if (npc.escapeAngleBias < -Math.PI) npc.escapeAngleBias += Math.PI * 2;
        } else {
          // Moving fine — slowly decay bias back toward neutral.
          npc.escapeAngleBias *= 0.6;
        }
        npc.lastCheckMs = nowMs;
        npc.lastX = npc.x;
        npc.lastZ = npc.z;
      }
    } else if (npc.escapeAngleBias !== 0) {
      // Returned to wander — clear any accumulated bias.
      npc.escapeAngleBias = 0;
      npc.lastCheckMs = 0;
    }

    // Silence lint: beforeX/beforeZ are now only used via the stuck
    // check indirection; keep the vars to make the intent visible in
    // future refactors (e.g. collider-bounce).
    void beforeX; void beforeZ;
  }

  snapshot(nowMs: number): NpcSnap[] {
    return this.npcs.map((n) => ({
      id: n.id,
      x: n.x,
      z: n.z,
      yaw: n.yaw,
      stage: stageFromMood(n.mood),
      mood: n.mood,
      shiny: nowMs < n.shinyUntilMs,
      visible: n.respawnAtMs === 0,
    }));
  }
}

function randRange(a: number, b: number): number {
  return a + Math.random() * (b - a);
}
